import { getSiteApi } from '@/api-calls/sites/get'
import { updateSiteApi } from '@/api-calls/sites/mutate'
import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import { updateSiteSchema } from '@/lib/schema/sites'
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Skeleton,
  Textarea,
} from '@blogging/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { queryClient, useFetch, useMutation } from '@ngtrvu/use-api'
import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { SectionHeading } from './section-heading'

export function SiteSettingsPage() {
  const { siteId } = useParams()
  const navigate = useNavigate()
  const form = useForm({
    resolver: zodResolver(updateSiteSchema),
    defaultValues: {
      name: '',
      title: '',
      slug: '',
      description: '',
      tagline: '',
      metaTitle: '',
      metaDescription: '',
    },
  })

  const {
    data: site,
    isLoading,
    error,
  } = useFetch(getSiteApi, {
    resourceName: 'site',
    initialParams: { id: siteId as string },
  })

  const { mutate: updateSite, isPending: isUpdating } = useMutation(
    updateSiteApi,
    {
      resourceName: 'site',
      onSuccess: (data: any) => {
        queryClient.invalidateQueries({
          queryKey: [getSiteApi.apiName, { id: siteId }],
        })
        toast.success('Site settings updated successfully!')

        // If the slug changed and the current URL references the old slug,
        // navigate to the new slug so the page keeps resolving.
        const newSlug = data?.site?.slug
        if (newSlug && siteId === site?.slug && newSlug !== site?.slug) {
          navigate(`/${newSlug}/settings`, { replace: true })
        }
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to update site settings')
      },
    },
  )

  // Update form values when site data loads
  useEffect(() => {
    if (site && !isLoading) {
      form.reset({
        name: site.name ?? '',
        title: site.title ?? '',
        slug: site.slug ?? '',
        description: site.description ?? '',
        tagline: site.tagline ?? '',
        metaTitle: site.metaTitle ?? '',
        metaDescription: site.metaDescription ?? '',
      })
    }
  }, [site, isLoading, form])

  const onSubmit = async (data: any) => {
    try {
      updateSite({
        id: siteId as string,
        ...data,
      })
    } catch (error) {
      console.error('Failed to update site:', error)
    }
  }

  if (isLoading) {
    return (
      <ContainerWithSidebarAndNoTopbar>
        <Skeleton className="h-48 w-full rounded-lg" />
      </ContainerWithSidebarAndNoTopbar>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center">
        <p className="text-destructive">Error loading site settings</p>
        <Button variant="outline" onClick={() => navigate(0)} className="mt-2">
          Retry
        </Button>
      </div>
    )
  }

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-6">
        <div className="flex flex-col space-y-1">
          <h1 className="text-2xl font-bold">Site Settings</h1>
          <p className="text-sm text-muted-foreground">
            Manage your site's basic information and configuration.
          </p>
        </div>

        <div className="rounded-lg border bg-card p-5">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="divide-y">
              {/* Basic Information */}
              <div className="space-y-4 pb-5">
                <SectionHeading>Basic Information</SectionHeading>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Awesome" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input placeholder="Awesome Blogging CMS" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="slug"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Slug</FormLabel>
                        <FormControl>
                          <Input placeholder="awesome" {...field} />
                        </FormControl>
                        <FormDescription>
                          <span className="font-mono">
                            {field.value}.blgg.dev
                          </span>
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tagline"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tagline</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Your site's catchy tagline"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="A brief description of your site..."
                          {...field}
                          rows={2}
                        />
                      </FormControl>
                      <FormDescription>
                        Shown in search results and social previews.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Metadata */}
              <div className="space-y-4 py-5">
                <SectionHeading>Metadata</SectionHeading>

                <FormField
                  control={form.control}
                  name="metaTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Meta Title</FormLabel>
                      <FormControl>
                        <Input placeholder="SEO optimized title" {...field} />
                      </FormControl>
                      <FormDescription>
                        Keep it under 60 characters for best results.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="metaDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Meta Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="A compelling description for search engines..."
                          {...field}
                          rows={2}
                        />
                      </FormControl>
                      <FormDescription>
                        Keep it under 155 characters for best results.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex flex-col items-start gap-3 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Make sure to save your changes before leaving this page.
                </p>
                <Button
                  type="submit"
                  disabled={isUpdating || !form.formState.isDirty}
                  className="w-full sm:w-auto"
                >
                  {isUpdating && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </ContainerWithSidebarAndNoTopbar>
  )
}
